import MetricsChatService, {
  MetricsChatSummary,
  MetricsChatDetail,
} from '../../services/MetricsChatService';

const METRICS_CHAT_POLL_INTERVAL_MS = 1000;
const METRICS_CHAT_MAX_POLL_ATTEMPTS = 60;

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function hasPersistedAnswer(
  chat: MetricsChatDetail,
  sourceMessageId: number
): boolean {
  return chat.messages.some(
    (message) =>
      message.role === 'assistant' &&
      message.aiQuerySourceMessageId === sourceMessageId
  );
}

export interface MetricsChatState {
  chats: MetricsChatSummary[];
  activeChat: MetricsChatDetail | null;
  loading: boolean;
  sending: boolean;
  error: string | null;
}

export default {
  state: {
    chats: [] as MetricsChatSummary[],
    activeChat: null as MetricsChatDetail | null,
    loading: false,
    sending: false,
    error: null as string | null,
  } as MetricsChatState,

  mutations: {
    SET_METRICS_CHAT_CHATS(
      state: MetricsChatState,
      chats: MetricsChatSummary[]
    ) {
      state.chats = chats;
    },
    SET_METRICS_CHAT_ACTIVE_CHAT(
      state: MetricsChatState,
      chat: MetricsChatDetail | null
    ) {
      state.activeChat = chat;
    },
    REMOVE_METRICS_CHAT(state: MetricsChatState, chatId: number) {
      state.chats = state.chats.filter((c) => c.metricsChatId !== chatId);
      if (state.activeChat?.metricsChatId === chatId) {
        state.activeChat = null;
      }
    },
    SET_METRICS_CHAT_LOADING(state: MetricsChatState, val: boolean) {
      state.loading = val;
    },
    SET_METRICS_CHAT_SENDING(state: MetricsChatState, val: boolean) {
      state.sending = val;
    },
    SET_METRICS_CHAT_ERROR(state: MetricsChatState, error: string | null) {
      state.error = error;
    },
  },

  actions: {
    async fetchMetricsChats(context: any) {
      context.commit('SET_METRICS_CHAT_LOADING', true);
      context.commit('SET_METRICS_CHAT_ERROR', null);
      try {
        const chats = await MetricsChatService.getChats();
        context.commit('SET_METRICS_CHAT_CHATS', chats);
      } catch (e: any) {
        console.error('[MetricsChatStore] Failed to load chats:', e);
        context.commit('SET_METRICS_CHAT_ERROR', 'Failed to load chats');
      } finally {
        context.commit('SET_METRICS_CHAT_LOADING', false);
      }
    },

    async createMetricsChat(context: any, question: string) {
      context.commit('SET_METRICS_CHAT_SENDING', true);
      context.commit('SET_METRICS_CHAT_ERROR', null);

      try {
        const response = await MetricsChatService.createChat(question);
        const detail = await MetricsChatService.getChatDetail(
          response.metricsChatId
        );
        context.commit('SET_METRICS_CHAT_ACTIVE_CHAT', detail);
        await context.dispatch('fetchMetricsChats');

        const received = await context.dispatch('waitForMetricsChatAnswer', {
          chatId: response.metricsChatId,
          sourceMessageId: response.sourceMessageId,
        });
        if (!received) {
          context.commit(
            'SET_METRICS_CHAT_ERROR',
            'The answer is still processing. Reopen the conversation to check again.'
          );
        }

        return response;
      } catch (e: any) {
        console.error('[MetricsChatStore] Failed to create chat:', e);
        context.commit(
          'SET_METRICS_CHAT_ERROR',
          'Failed to get an answer. Please try again.'
        );
      } finally {
        context.commit('SET_METRICS_CHAT_SENDING', false);
      }
    },

    async loadMetricsChat(context: any, chatId: number) {
      context.commit('SET_METRICS_CHAT_LOADING', true);
      context.commit('SET_METRICS_CHAT_ERROR', null);
      try {
        const detail = await MetricsChatService.getChatDetail(chatId);
        context.commit('SET_METRICS_CHAT_ACTIVE_CHAT', detail);
      } catch (e: any) {
        console.error('[MetricsChatStore] Failed to load chat:', e);
        context.commit('SET_METRICS_CHAT_ERROR', 'Failed to load chat');
      } finally {
        context.commit('SET_METRICS_CHAT_LOADING', false);
      }
    },

    async sendMetricsChatMessage(context: any, question: string) {
      const state: MetricsChatState = context.state;
      if (!state.activeChat) return;

      context.commit('SET_METRICS_CHAT_SENDING', true);
      context.commit('SET_METRICS_CHAT_ERROR', null);

      try {
        const response = await MetricsChatService.sendMessage(
          state.activeChat.metricsChatId,
          question
        );
        const received = await context.dispatch('waitForMetricsChatAnswer', {
          chatId: response.metricsChatId,
          sourceMessageId: response.sourceMessageId,
        });
        if (!received) {
          context.commit(
            'SET_METRICS_CHAT_ERROR',
            'The answer is still processing. Reopen the conversation to check again.'
          );
        }
      } catch (e: any) {
        console.error('[MetricsChatStore] Failed to send message:', e);
        context.commit(
          'SET_METRICS_CHAT_ERROR',
          'Failed to get an answer. Please try again.'
        );
      } finally {
        context.commit('SET_METRICS_CHAT_SENDING', false);
      }
    },

    async waitForMetricsChatAnswer(
      context: any,
      payload: { chatId: number; sourceMessageId: number }
    ): Promise<boolean> {
      for (
        let attempt = 0;
        attempt < METRICS_CHAT_MAX_POLL_ATTEMPTS;
        attempt += 1
      ) {
        const detail = await MetricsChatService.getChatDetail(payload.chatId);
        const state: MetricsChatState = context.state;
        if (state.activeChat?.metricsChatId === payload.chatId) {
          context.commit('SET_METRICS_CHAT_ACTIVE_CHAT', detail);
        }

        if (hasPersistedAnswer(detail, payload.sourceMessageId)) {
          return true;
        }

        if (attempt < METRICS_CHAT_MAX_POLL_ATTEMPTS - 1) {
          await wait(METRICS_CHAT_POLL_INTERVAL_MS);
        }
      }

      return false;
    },

    async deleteMetricsChat(context: any, chatId: number) {
      try {
        await MetricsChatService.deleteChat(chatId);
        context.commit('REMOVE_METRICS_CHAT', chatId);
      } catch (e: any) {
        console.error('[MetricsChatStore] Failed to delete chat:', e);
      }
    },

    async updateMetricsChatTitle(
      context: any,
      payload: { chatId: number; title: string }
    ) {
      try {
        await MetricsChatService.updateTitle(payload.chatId, payload.title);
        // Update in the chats list
        const state: MetricsChatState = context.state;
        const chat = state.chats.find(
          (c) => c.metricsChatId === payload.chatId
        );
        if (chat) chat.title = payload.title;
        // Update active chat if it's the same one
        if (state.activeChat?.metricsChatId === payload.chatId) {
          state.activeChat.title = payload.title;
        }
      } catch (e: any) {
        console.error('[MetricsChatStore] Failed to update title:', e);
      }
    },
  },
};
